import PageSkeleton from "components/Skeleton/PageSkeleton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useEffect, useState } from "react";
import { FiArrowLeft, FiFileText } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getExpensesById } from "utils/api";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";
import RecordExpensePaymentDialog from "./RecordExpensePaymentDialog";
import "./expenseDetails.css";

const formatExpenseDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value || "—"
    : date.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
};

function ExpenseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("payments");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      await getExpensesById(
        { _id: id },
        (data) => setExpense(data?.Result_Output?.[0] || null),
        dispatch
      );
      setLoading(false);
    })();
  }, [dispatch, id]);

  if (!expense)
    return (
      <DashboardLayout>
        <main className="expense-details">
          <button
            className="expense-back"
            type="button"
            onClick={() => navigate("/expenses")}
          >
            <FiArrowLeft />
            Back to Expenses
          </button>
          {loading ? (
            <PageSkeleton
              type="detail"
              variant="expense"
              label="Loading expense details…"
            />
          ) : (
            <div className="expense-detail-empty">Expense not found.</div>
          )}
        </main>
      </DashboardLayout>
    );

  const total = Number(expense.totalAmount) || 0;
  const tax = Number(expense.taxAmount) || 0;
  const paid = Number(expense.paidAmount || expense.paymentAmount) || 0;
  const remaining = Math.max(
    0,
    Number.isFinite(Number(expense.outstandingAmount))
      ? Number(expense.outstandingAmount)
      : total - paid
  );
  const receiptName =
    expense.uploadDocument || expense.receipt || expense.receiptFileName;
  const receiptUrl = receiptName
    ? `${
        appConstants.baseURL
      }expenses/viewDocument?fileName=${encodeURIComponent(receiptName)}`
    : "";

  return (
    <DashboardLayout>
      <main className="expense-details">
        <button
          className="expense-back"
          type="button"
          onClick={() => navigate("/expenses")}
        >
          <FiArrowLeft />
          Back to Expenses
        </button>
        <header className="expense-details__header">
          <div>
            <h1>Expense details</h1>
            <p>
              {expense.referenceNumber || expense._id} ·{" "}
              {expense.companyName || expense.name}
            </p>
            <div className="expense-detail-badges">
              <span>Saved / Posted</span>
              <span className="warning">
                {expense.paymentStatus || "Unpaid"}
              </span>
              <span className={receiptName ? "" : "warning"}>
                {receiptName ? "Attached" : "Receipt missing"}
              </span>
            </div>
          </div>
          <div className="expense-details__actions">
            {expense.contactTypeId && (
              <button
                className="expense-detail-secondary"
                type="button"
                onClick={() => navigate(`/contacts/${expense.contactTypeId}`)}
              >
                View supplier
              </button>
            )}
            {remaining > 0 && (
              <button
                className="expense-primary"
                type="button"
                onClick={() => setPaymentDialogOpen(true)}
              >
                Record payment
              </button>
            )}
          </div>
        </header>
        <div className="expense-details__layout">
          <div className="expense-details__main">
            <section className="expense-detail-card">
              <h2>Purchase details</h2>
              <h3>{expense.companyName || expense.name || "Supplier"}</h3>
              <p className="expense-detail-reference">
                {expense.referenceNumber || expense._id}
              </p>
              <div className="expense-detail-grid">
                <div>
                  <span>Expense date</span>
                  <strong>{formatExpenseDate(expense.expenseDate)}</strong>
                </div>
                <div>
                  <span>Category</span>
                  <strong>{expense.categoryCodeName || "—"}</strong>
                </div>
                <div className="wide">
                  <span>Supplier reference / description</span>
                  <strong>
                    {expense.referenceNumber || expense.notes || "—"}
                  </strong>
                </div>
              </div>
              <dl className="expense-detail-money">
                <div>
                  <dt>Excluding GST</dt>
                  <dd>${formatCurrencyAUD(total - tax)}</dd>
                </div>
                <div>
                  <dt>GST recorded</dt>
                  <dd>${formatCurrencyAUD(tax)}</dd>
                </div>
                <div>
                  <dt>Total amount · AUD</dt>
                  <dd>${formatCurrencyAUD(total)}</dd>
                </div>
              </dl>
            </section>
            <section className="expense-detail-card">
              <h2>Receipt &amp; tax evidence</h2>
              {receiptName ? (
                <>
                  <span className="expense-detail-badge">Attached</span>
                  <p>{receiptName}</p>
                  <button
                    className="expense-detail-secondary"
                    type="button"
                    onClick={() => setViewerOpen(true)}
                  >
                    <FiFileText />
                    View receipt
                  </button>
                </>
              ) : (
                <>
                  <span className="expense-detail-badge warning">Missing</span>
                  <h3>No receipt attached</h3>
                  <p>
                    Attach supporting evidence to this expense. Adding a receipt
                    does not create another expense or payment.
                  </p>
                  <button
                    className="expense-detail-secondary"
                    type="button"
                    onClick={() => navigate(`/expenses/editExpenses/${id}`)}
                  >
                    Add receipt
                  </button>
                </>
              )}
            </section>
            <nav className="expense-detail-tabs">
              <button
                className={activeTab === "payments" ? "active" : ""}
                onClick={() => setActiveTab("payments")}
                type="button"
              >
                Payments
              </button>
              <button
                className={activeTab === "adjustments" ? "active" : ""}
                onClick={() => setActiveTab("adjustments")}
                type="button"
              >
                Adjustments
              </button>
              <button
                className={activeTab === "activity" ? "active" : ""}
                onClick={() => setActiveTab("activity")}
                type="button"
              >
                Activity &amp; notes
              </button>
            </nav>
            {activeTab === "payments" && (
              <section className="expense-detail-card">
                <h2>Payment history</h2>
                <div className="expense-detail-balance">
                  <span>Payments recorded</span>
                  <strong>${formatCurrencyAUD(paid)}</strong>
                </div>
                <h3>{paid ? "Payment recorded" : "No payments recorded"}</h3>
                <p>
                  Record an outgoing payment only after money was paid
                  elsewhere.
                </p>
                {expense.payments?.length > 0 && (
                  <div className="expense-payment-history">
                    {expense.payments.map((payment) => (
                      <div key={payment._id || payment.createdAt}>
                        <span>
                          {new Date(payment.paymentDate).toLocaleDateString(
                            "en-AU",
                            { day: "numeric", month: "short", year: "numeric" }
                          )}
                          {payment.paymentMethod
                            ? ` · ${payment.paymentMethod}`
                            : ""}
                        </span>
                        <strong>${formatCurrencyAUD(payment.amount)}</strong>
                        {payment.reference && (
                          <small>{payment.reference}</small>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
            {activeTab === "adjustments" && (
              <section className="expense-detail-card">
                <h2>Linked supplier adjustments</h2>
                <div className="expense-detail-balance">
                  <span>Confirmed reductions applied</span>
                  <strong>$0.00</strong>
                </div>
                <h3>No linked adjustment or supplier credit memo</h3>
                <p>
                  Adjustments require a supported backend workflow before they
                  can alter this balance.
                </p>
              </section>
            )}
            {activeTab === "activity" && (
              <section className="expense-detail-card">
                <h2>Activity &amp; private internal notes</h2>
                <p>
                  Expense saved and posted. Saved financial values are protected
                  from silent changes.
                </p>
                {expense.notes && (
                  <div className="expense-detail-note">
                    <strong>Internal note</strong>
                    <span>{expense.notes}</span>
                  </div>
                )}
              </section>
            )}
          </div>
          <aside className="expense-details__aside">
            <section className="expense-detail-card">
              <h2>Remaining balance</h2>
              <strong className="expense-detail-total">
                ${formatCurrencyAUD(remaining)}
              </strong>
              <p>AUD · {formatExpenseDate(expense.expenseDate)}</p>
            </section>
            <section className="expense-detail-card">
              <h2>Payment summary</h2>
              <dl className="expense-detail-money">
                <div>
                  <dt>Expense total</dt>
                  <dd>${formatCurrencyAUD(total)}</dd>
                </div>
                <div>
                  <dt>Payments recorded</dt>
                  <dd>${formatCurrencyAUD(paid)}</dd>
                </div>
                <div>
                  <dt>Applied reductions</dt>
                  <dd>$0.00</dd>
                </div>
              </dl>
              {remaining > 0 && (
                <button
                  className="expense-primary expense-record-payment"
                  type="button"
                  onClick={() => setPaymentDialogOpen(true)}
                >
                  Record payment
                </button>
              )}
              <p>
                Record payment records a payment made elsewhere. It does not
                transfer money.
              </p>
            </section>
          </aside>
        </div>
      </main>
      {viewerOpen && (
        <div
          className="expense-viewer document-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Receipt viewer"
        >
          <div>
            <header>
              <strong>{receiptName}</strong>
              <button type="button" onClick={() => setViewerOpen(false)}>
                Close
              </button>
            </header>
            <iframe title="Receipt viewer" src={receiptUrl} />
          </div>
        </div>
      )}
      {paymentDialogOpen && (
        <RecordExpensePaymentDialog
          expense={{ ...expense, outstandingAmount: remaining }}
          onClose={() => setPaymentDialogOpen(false)}
          onRecorded={(result) =>
            setExpense((current) => ({
              ...current,
              paidAmount: result.paidAmount,
              outstandingAmount: result.outstandingAmount,
              paymentStatus: result.paymentStatus,
              payments: [...(current.payments || []), result.payment],
            }))
          }
        />
      )}
    </DashboardLayout>
  );
}
export default ExpenseDetails;
